import * as THREE from 'three'
import * as CANNON from 'cannon-es'
import Experience from '../Experience.js'
import SimpleCarModel from './SimpleCarModel.js'
import SimpleCarPhysics from './SimpleCarPhysics.js'
import { gridPose } from '../Race/Track.js'
import DrivingEffects from './DrivingEffects.js'

let instance = null

export default class SimpleCar {
    constructor() {
        // Singleton
        if (instance) {
            return instance
        }

        instance = this

        this.experience = new Experience()
        this.scene = this.experience.scene

        this.simpleCarModel = new SimpleCarModel()
        this.simpleCarPhysics = new SimpleCarPhysics()

        this.vehicle = this.simpleCarPhysics.vehicle

        this.time = this.experience.time

        this.frontWheelMesh1 = this.simpleCarModel.frontWheelMesh1
        this.frontWheelMesh2 = this.simpleCarModel.frontWheelMesh2
        this.rearWheelMesh1 = this.simpleCarModel.rearWheelMesh1
        this.rearWheelMesh2 = this.simpleCarModel.rearWheelMesh2
        this.chassisMesh = this.simpleCarModel.chassisMesh
        
        // For camera tracking
        this.forwardDirection = new THREE.Vector3(0, 0, 1)
        this.tempQuat = new THREE.Quaternion()
        this.drivingEffects = new DrivingEffects(this.experience)
        this.lastVelocity = new THREE.Vector3()
        this.bodyLean = { pitch: 0, roll: 0 }
        this.bodyLeanTarget = { pitch: 0, roll: 0 }
        this._hasLastVelocity = false
    }
    
    // Get the car's forward direction for camera
    getForwardDirection() {
        if (!this.chassisMesh) return this.forwardDirection
        
        // Get forward direction from chassis rotation
        this.tempQuat.copy(this.chassisMesh.quaternion)
        this.forwardDirection.set(0, 0, 1)
        this.forwardDirection.applyQuaternion(this.tempQuat)
        this.forwardDirection.y = 0
        this.forwardDirection.normalize()
        
        return this.forwardDirection
    }
    
    // Get car position
    getPosition() {
        if (!this.chassisMesh) return new THREE.Vector3(5, 0, 0)
        return this.chassisMesh.position.clone()
    }
    
    // Update car color from settings
    updateColor(colorHex) {
        this.simpleCarModel.setColor(colorHex)
    }

    // Troca carro/skin (id do catálogo)
    setCar(carId, color) {
        return this.simpleCarModel.applyCar(carId, color)
    }

    // Troca personagem/piloto (id do catálogo de characters)
    setCharacter(characterId) {
        return this.simpleCarModel.applyCharacter(characterId)
    }

    // Põe o kart (chassi + rodas) em x,z olhando para `yaw`, parado
    placeAt(x, z, yaw) {
        const chassis = this.vehicle.chassisBody
        if (!chassis) return
        chassis.velocity.setZero()
        chassis.angularVelocity.setZero()
        chassis.position.set(x, 1.6, z)
        chassis.quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), yaw)

        // rodas junto (senão as juntas puxam o chassi de volta)
        const fallback = [[1, -0.55, -1], [-1, -0.55, -1], [1, -0.55, 1.2], [-1, -0.55, 1.2]]
        const tmp = new CANNON.Vec3()
        this.vehicle.wheelBodies?.forEach((wheel, i) => {
            const pv = this.vehicle.constraints?.[i]?.pivotA
            const local = pv || new CANNON.Vec3(...fallback[i])
            chassis.quaternion.vmult(local, tmp)
            wheel.position.set(x + tmp.x, 1.6 + tmp.y, z + tmp.z)
            wheel.quaternion.copy(chassis.quaternion)
            wheel.velocity.setZero()
            wheel.angularVelocity.setZero()
            wheel.wakeUp()
        })
        chassis.wakeUp()
    }

    // Volta pro grid de largada (gridSlot = vaga; 0 = pole)
    resetPosition() {
        const p = gridPose(typeof this.gridSlot === 'number' ? this.gridSlot : 0)
        this.placeAt(p.x, p.z, p.yaw)
    }

    update() {
        // O FOV acompanha a velocidade real do chassi para dar sensação de aceleração.
        const velocity = this.vehicle?.chassisBody?.velocity
        if (velocity && this.experience.camera?.setDrivingSpeed) {
            const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y + velocity.z * velocity.z)
            this.experience.camera.setDrivingSpeed(speed)
        }

        if (this.chassisMesh) {
            this.simpleCarModel.updateMeshPosition(this.chassisMesh, this.vehicle.chassisBody)
        }

        // A carroceria inclina levemente com aceleração/frenagem e mudanças bruscas de direção.
        // A física permanece intacta; é apenas uma camada visual sobre o grupo da carroceria.
        if (velocity && this.simpleCarModel.bodyGroup) {
            const dt = Math.min(0.05, Math.max(0.001, (this.time?.delta || 16) / 1000))
            const vx = velocity.x, vz = velocity.z
            let ax = 0, az = 0
            if (this._hasLastVelocity) {
                ax = (vx - this.lastVelocity.x) / dt
                az = (vz - this.lastVelocity.z) / dt
            }
            this.lastVelocity.set(vx, velocity.y, vz)
            this._hasLastVelocity = true
            this.bodyLeanTarget.roll = THREE.MathUtils.clamp(-ax * 0.0045, -0.075, 0.075)
            this.bodyLeanTarget.pitch = THREE.MathUtils.clamp(az * 0.0025, -0.045, 0.045)
            const leanAlpha = 1 - Math.exp(-dt * 5.5)
            this.bodyLean.roll += (this.bodyLeanTarget.roll - this.bodyLean.roll) * leanAlpha
            this.bodyLean.pitch += (this.bodyLeanTarget.pitch - this.bodyLean.pitch) * leanAlpha
            this.simpleCarModel.bodyGroup.rotation.z = this.bodyLean.roll
            this.simpleCarModel.bodyGroup.rotation.x = this.bodyLean.pitch
        }

        if (this.frontWheelMesh1) {
            this.simpleCarModel.updateMeshPosition(this.frontWheelMesh1, this.vehicle.wheelBodies[2])
        }

        if (this.frontWheelMesh2) {
            this.simpleCarModel.updateMeshPosition(this.frontWheelMesh2, this.vehicle.wheelBodies[3])
        }

        if (this.rearWheelMesh1) {
            this.simpleCarModel.updateMeshPosition(this.rearWheelMesh1, this.vehicle.wheelBodies[0])
        }

        if (this.rearWheelMesh2) {
            this.simpleCarModel.updateMeshPosition(this.rearWheelMesh2, this.vehicle.wheelBodies[1])
        }
        
        // Update camera to follow car
        if (!this.experience.camera.testingMode && this.chassisMesh) {
            this.experience.camera.followTarget(
                this.chassisMesh.position,
                this.getForwardDirection()
            )
        }

        // Marcas de pneu, fumaça ao derrapar e rastro de nitro.
        this.drivingEffects?.update(this, this.time?.delta || 16)
    }
}